/**
 * Clock-aligned reminder intervals for Frequency badges and SIPS.
 *
 * The first deadline is active-start + interval (the opening reminder at start
 * is a nudge, not a miss). Completed count at 18:58 with 07:00 start / 45 min
 * is 15, not 16–17 from treating 07:00 as a miss or double-ticking.
 */

export function hhmmToMinutes(hhmm: string): number {
  const [hours, minutes] = String(hhmm || '00:00')
    .split(':')
    .map((part) => Number.parseInt(part, 10) || 0);
  return hours * 60 + minutes;
}

export function activeMinutesBetween(startTime: string, endTime: string): number {
  const start = hhmmToMinutes(startTime);
  const end = hhmmToMinutes(endTime);
  if (end <= start) {
    return 24 * 60 - start + end;
  }
  return end - start;
}

export function totalReminderSlots(
  startTime: string,
  endTime: string,
  intervalMin: number
): number {
  if (intervalMin <= 0) return 0;
  return Math.floor(activeMinutesBetween(startTime, endTime) / intervalMin);
}

/** Minutes from midnight in the given IANA timezone. */
export function zonedMinutesOfDay(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0);
  return hour * 60 + minute;
}

/**
 * How many reminder intervals have fully elapsed (deadlines passed).
 * Does not count the opening slot at active-hours start as completed.
 */
export function completedReminderIntervals(
  startTime: string,
  endTime: string,
  intervalMin: number,
  nowMinutes: number
): number {
  const total = totalReminderSlots(startTime, endTime, intervalMin);
  if (total <= 0 || intervalMin <= 0) return 0;

  const startM = hhmmToMinutes(startTime);
  const endM = hhmmToMinutes(endTime);
  const overnight = endM <= startM;

  let current = nowMinutes;
  let endLimit = endM;
  if (overnight) {
    if (current < startM) current += 24 * 60;
    endLimit = endM + 24 * 60;
  }

  if (current < startM) return 0;

  const firstDeadline = startM + intervalMin;
  if (current < firstDeadline) return 0;

  const cappedNow = Math.min(current, endLimit);
  const completed = Math.floor((cappedNow - firstDeadline) / intervalMin) + 1;
  return Math.max(0, Math.min(total, completed));
}

/**
 * Deadlines that have elapsed since the tracked `nextDeadline`, i.e. reminder
 * intervals the scheduler never recorded. Returns undefined when there is no
 * deadline to measure against, so callers fall back to the clock grid.
 */
export function pendingIntervalsSinceDeadline(
  nextDeadline: Date | string | null | undefined,
  intervalMin: number,
  now: Date = new Date()
): number | undefined {
  if (!nextDeadline || intervalMin <= 0) return undefined;
  const deadline = new Date(nextDeadline);
  if (Number.isNaN(deadline.getTime())) return undefined;

  const overdueMs = now.getTime() - deadline.getTime();
  if (overdueMs < 0) return 0;
  return Math.floor(overdueMs / (intervalMin * 60000)) + 1;
}

export function reconcileFrequencyTracking(input: {
  missCount?: number;
  successfulIntakes: number;
  reminderCount: number;
  completedIntervals: number;
  /** Deadlines elapsed past the tracked nextDeadline; omit when unknown. */
  pendingIntervals?: number;
  /** Reminder slots in the whole active window. The score can never exceed it. */
  totalIntervals?: number;
}): { missCount: number; reminderCount: number } {
  const successful = Math.max(0, Math.floor(input.successfulIntakes || 0));
  const completed = Math.max(0, Math.floor(input.completedIntervals || 0));
  const stored = Math.max(0, Math.floor(input.reminderCount || 0));
  const pending =
    input.pendingIntervals == null ? null : Math.max(0, Math.floor(input.pendingIntervals));

  // Logging resets the deadline, so the tracked count — not the fixed clock grid —
  // is the real number of intervals that opened. The grid only fills in slots the
  // scheduler failed to record, which keeps the dial equal to the notification.
  const intervals =
    pending == null
      ? Math.max(stored, completed)
      : Math.max(stored, Math.min(stored + pending, completed));

  // Only a log can open an interval ahead of the grid, so grid + logs bounds how
  // far the stored count may legitimately run (caps scheduler double-ticks).
  const driftCeiling = Math.max(completed + successful, successful);
  const dayCeiling =
    input.totalIntervals && input.totalIntervals > 0
      ? Math.max(input.totalIntervals, successful)
      : Number.POSITIVE_INFINITY;

  const reminderCount = Math.max(successful, Math.min(intervals, driftCeiling, dayCeiling));
  // Every interval is either a sip or a miss. A deadline that was missed and then
  // logged late is a sip, so misses are derived here instead of being added to
  // successes — summing them is what turned 12 sips over 17 intervals into 12/29.
  const missCount = Math.max(0, reminderCount - successful);
  return { missCount, reminderCount };
}

/** Shift a YYYY-MM-DD calendar date by whole days (timezone-independent). */
export function shiftIsoDate(date: string, days: number): string {
  const [year, month, day] = String(date)
    .split('-')
    .map((part) => Number.parseInt(part, 10) || 0);
  const shifted = new Date(Date.UTC(year, (month || 1) - 1, (day || 1) + days));
  return shifted.toISOString().slice(0, 10);
}

/** Next deadline minutes-from-midnight after `nowMinutes` (first is start+interval). */
export function nextDeadlineMinutes(
  startTime: string,
  endTime: string,
  intervalMin: number,
  nowMinutes: number
): number | null {
  if (intervalMin <= 0) return null;
  const startM = hhmmToMinutes(startTime);
  const endM = hhmmToMinutes(endTime);
  const overnight = endM <= startM;
  let endLimit = overnight ? endM + 24 * 60 : endM;
  let current = nowMinutes;
  if (overnight && current < startM) current += 24 * 60;

  let deadline = startM + intervalMin;
  if (current < startM) return deadline;

  while (deadline <= current) {
    deadline += intervalMin;
  }
  if (deadline > endLimit) return null;
  return deadline % (24 * 60);
}
